import { LoadingOutlined } from '@ant-design/icons';
import {
  GpsFixIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
} from '@phosphor-icons/react';

import { stepZoom } from '@/lib/maps/zoom';

import styles from './map-control-stack.module.scss';

export type MapControlStackProps = {
  /** The map's current zoom — decides which zoom control is unavailable (113584 §6c). */
  zoom: number;
  /** Asked for the next zoom level. Never called when the step would leave the supported range. */
  onZoom: (zoom: number) => void;
  /**
   * The Grower asked for their location (113583 §14b-§14e; 113584 §9b-§9g). Unset ⇒ the control
   * still renders, still enabled, and does nothing — which is what keeps §14a ("displayed and
   * enabled regardless of permission state") true on every page that mounts the stack.
   */
  onMyLocation?: () => void;
  /**
   * A position request is in flight (113584 §9c) — the control shows a loading state and **stays
   * enabled**. The hook, not the button, is what stops a second request: 113583 §14a admits no
   * disabled state for this control at all.
   */
  myLocationBusy?: boolean;
};

/**
 * The map's control stack, bottom right (113584 §2a): My location above zoom in / zoom out
 * (Figma `1322:689046` at y=654 over `1322:689041` at y=703).
 *
 * Plain `<button>`s absolutely positioned inside the map **area**, not Google `<MapControl>`
 * children — three consequences, all of them acceptance criteria: the SDK stays confined to
 * `map-canvas.tsx`/`map-overlay.tsx` (ADR-0017); the controls cannot move when the Grower pans or
 * zooms, because they are not in the map's coordinate space (113584 §4a); and each is in the page's
 * normal focus order with Enter/Space activation for free (§4b).
 *
 * **No control reads permission state**, which is how "displayed and enabled regardless of
 * permission state" (113583 §14a, §15a) holds — there is nothing to get wrong. The My location
 * control has no refused, blocked or unavailable appearance either (§6b): the Grower discovers that
 * location is unavailable only by pressing it, at which point ticket 14 raises the relevant modal.
 */
export function MapControlStack({
  zoom,
  onZoom,
  onMyLocation,
  myLocationBusy,
}: MapControlStackProps) {
  // `stepZoom` returns the zoom it was given when the step would leave the supported range, so
  // equality IS the limit test — no second predicate to keep in step with the first.
  const zoomedIn = stepZoom(zoom, 1);
  const zoomedOut = stepZoom(zoom, -1);

  return (
    <div className={styles.stack}>
      <button
        type="button"
        className={styles.locate}
        aria-label="My location"
        aria-busy={myLocationBusy ?? false}
        onClick={onMyLocation}
      >
        {myLocationBusy ? (
          <LoadingOutlined aria-hidden style={{ fontSize: 20 }} />
        ) : (
          <GpsFixIcon size={20} aria-hidden />
        )}
      </button>

      <div className={styles.group}>
        <button
          type="button"
          className={styles.control}
          aria-label="Zoom in"
          disabled={zoomedIn === zoom}
          onClick={() => onZoom(zoomedIn)}
        >
          <MagnifyingGlassPlusIcon size={20} aria-hidden />
        </button>
        <button
          type="button"
          className={styles.control}
          aria-label="Zoom out"
          disabled={zoomedOut === zoom}
          onClick={() => onZoom(zoomedOut)}
        >
          <MagnifyingGlassMinusIcon size={20} aria-hidden />
        </button>
      </div>
    </div>
  );
}
